import React, { Fragment } from 'react';

import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';

import CustomThemeButton from '@atlaskit/button/custom-theme-button';
import { Checkbox } from '@atlaskit/checkbox';
import { Box, Stack, Inline, Text } from '@atlaskit/primitives';

import { ActivityApi } from 'src/components/activity/types';
import scrollTo from 'src/redux/global/actions/scroll-to';
import { COLLAPSE_TASKS_CARD } from 'src/redux/pull-request/actions';
import { updateSidebarState } from 'src/redux/sidebar';

import { messages } from './pull-request-activity-events.i18n';
import * as styles from './pull-request-event.styled';

type Props = {
  event: ActivityApi['TaskActivity'];
};

type MultiTaskProps = {
  event: ActivityApi['TaskActivity'][];
};

export const PullRequestTaskEntry = React.memo(({ event }: Props) => {
  const dispatch = useDispatch();
  const { action } = event.task;
  const { content } = event.task.task;
  const openTaskCard = () => {
    const targetId = 'expanded-Tasks';
    dispatch(
      updateSidebarState({
        isOpen: true,
        tabIndex: 0,
        sidebarType: 'code-review',
      })
    );
    dispatch({
      type: COLLAPSE_TASKS_CARD,
      payload: false,
    });
    // Wait after task card has finished expanding
    setTimeout(
      () =>
        dispatch(
          scrollTo({
            targetId,
          })
        ),
      400
    );
  };

  let actionFormattedMessage;

  if (action === 'CREATED') {
    actionFormattedMessage = (
      <Fragment>
        <FormattedMessage {...messages.taskCreatedEventMessage} />{' '}
        <CustomThemeButton
          appearance="link"
          spacing="none"
          onClick={openTaskCard}
        >
          <FormattedMessage {...messages.taskEventMessage} />
        </CustomThemeButton>
      </Fragment>
    );
  } else {
    actionFormattedMessage = (
      <Fragment>
        <FormattedMessage {...messages.taskResolvedEventMessage} />{' '}
        <CustomThemeButton
          appearance="link"
          spacing="none"
          onClick={openTaskCard}
        >
          <FormattedMessage {...messages.taskEventMessage} />
        </CustomThemeButton>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <styles.MessageText>{actionFormattedMessage}</styles.MessageText>{' '}
      <styles.TaskSnippet>{content.raw}</styles.TaskSnippet>
    </Fragment>
  );
});

export const PullRequestTasksTitle = ({ event }: MultiTaskProps) => {
  const task = event[0];
  const { action } = task.task;
  const count = event.length;
  return (
    <FormattedMessage
      values={{ count }}
      {...(action === 'CREATED'
        ? messages.tasksCreatedEventTitle
        : messages.tasksResolvedEventTitle)}
    />
  );
};

export const PullRequestTasksBody = ({ event }: MultiTaskProps) => {
  // naming is hard
  const tasks = event;

  return (
    <Box paddingInlineStart="space.400">
      <Stack>
        {tasks.map((task, index) => {
          return (
            <Inline alignBlock="center" key={index}>
              <Checkbox
                isChecked={task.task.action === 'RESOLVED'}
                isDisabled
              />
              <Text>{task.task.task.content.raw}</Text>
            </Inline>
          );
        })}
      </Stack>
    </Box>
  );
};

export default PullRequestTaskEntry;
